import { Validators } from '@angular/forms';
import isFileExtension from './validators/is-valid-file-extension.validator';
import isFileSize from './validators/file-size.validator';
import { CustomTemplate } from './contracts/custom-template';
import { SafetyManagementService } from '../../safety-management/shared/services/safety-management.service';
import { map } from 'rxjs/operators';
import { ADVANCED_PLAN } from '../../../objects/subscription-plans';
import { LooseObject } from '../../../objects/loose-object';
import { Select } from '../../../objects/select';
import { clientLogoCustom } from '../admin/common-defaults/client-logo-custom';
import { data_is_true, data_get, when } from '../../../shared/utils/common';

export type FORM_TEMPLATE_DISPLAY_ORIENTATION = 'portrait' | 'landscape';

export type PAGE_FORMATS = 'letter' | 'legal' | 'a4';

export const FORM_TEMPLATE_DISPLAY_ORIENTATION_LIST: FORM_TEMPLATE_DISPLAY_ORIENTATION[] = [
  'portrait',
  'landscape',
];

export const PAGE_FORMATS_LIST: PAGE_FORMATS[] = [
  'a4',
  'letter',
  'legal'
];

export const COUNTRIES_WITH_PAGE_FORMAT_THAT_IS_NOT_A4 = {
  US: 'letter',
  PH: 'letter',
  CA: 'letter'
};

const SUPPORTED_MODULES = [
  'leads',
  'customers',
  'sites',
  'quotes',
  'jobs',
  'customer_invoices',
  'purchase_orders',
  'supplier_invoices',
  'opportunities'
];

const TEMPLATE_MAX_FILE_SIZE = '2MB';
const TEMPLATE_EXTENSIONS = ['odt'];

const TEMPLATE_FILE_RULES = [
  Validators.required,
  isFileExtension(TEMPLATE_EXTENSIONS),
  isFileSize(TEMPLATE_MAX_FILE_SIZE)
];

const TEMPLATE_FILE_RULES_UPDATE = [
  isFileExtension(TEMPLATE_EXTENSIONS),
  isFileSize(TEMPLATE_MAX_FILE_SIZE)
];

export {
  SUPPORTED_MODULES,
  TEMPLATE_MAX_FILE_SIZE,
  TEMPLATE_EXTENSIONS,
  TEMPLATE_FILE_RULES,
  TEMPLATE_FILE_RULES_UPDATE,
};


export const CUSTOM_MODULE_TEMPLATES: {[module: string]: CustomTemplate[]} = {
  jobs: [
    {
      label: "work_order",
      module: "jobs"
    },
    {
      label: "job_report",
      module: "jobs",
      filter: { type: 'job_report'}
    },
    {
      label: "swms_pdf",
      module: "job_swms",
      fn: (ctx, id) => ctx.get(SafetyManagementService).getPdf$(id).pipe(
        map(data => ({ body: data }))
      ),
      options: {
        preview_label: 'swms_pdf'
      },
      minimum_subscription_plan: ADVANCED_PLAN,
    }
  ],
  supplier_invoices: [
    {
        label: "supplier_invoice_report",
        module: "supplier_invoices"
    },
  ],
  assets: [
    {
    label: "asset_report",
    module: "assets"
    }
  ],
  customer_invoices: [
    {
        label: "customer_invoice_report",
        module: "customer_invoices"
    }
  ],
  purchase_orders: [
    {
        label: "purchase_order_report",
        module: "purchase_orders"
    },
  ]
};

export const DOCUMENT_TYPE_OPTIONS = {
  sites: [
      {
          id: 'site_asset_summary',
          text: 'asset_summary_report',
      },
      {
          id: 'site_annual_condition',
          text: 'annual_condition_report',
      }
  ],
  jobs: [
      {
          id: 'job_report',
          text: 'job_report',
      },
      {
          id: 'job_swms',
          text: 'swms_pdf',
      },
      {
          id: 'job_work_order',
          text: 'work_order',
      }
  ],
  checklists: [
      {
          id: 'asset_checklist',
          text: 'assets_checklist',
      },
      {
          id: 'job_checklist',
          text: 'job_checklist',
      }
  ],
  supplier_invoices: [
      {
          id: 'supplier_invoice_report',
          text: 'supplier_invoice_report',
      }
  ],
  assets: [
      {
          id: 'asset_report',
          text: 'asset_report',
      }
  ],
  customer_invoices: [
      {
          id: 'customer_invoice_report',
          text: 'customer_invoice_report',
      }
  ],
  purchase_orders: [
      {
          id: 'purchase_order_report',
          text: 'purchase_order_report',
      }
  ]
};

// Kindly add if there's any module you need to use
export const MODULE_RELATE_TEXT_FIELDS = {
  customer_id: 'customer_text',
  job_id: 'job_text',
  site_id : 'site_text',
  contact_id : 'contact_text',
  opportunity_id : 'opportunity_text',
  quote_id : 'quote_text',
  asset_id : 'asset_text',
  customer_invoice_id : 'customer_invoice_text',
  purchase_order_id : 'purchase_order_text',
  supplier_invoice_id : 'supplier_invoice_text',
  user_id : 'user_text',
  item_id : 'item_text',
  asset_type_id : 'asset_type_text',
  pricebook_id: 'pricebook_text',
  warehouse_id: 'warehouse_text',
};

/// contains the list of documents that can be generated from the html template
export const HTML_TEMPLATE_CAPABLE_DOCUMENTS = {
  'jobs': [
    'job_report',
    'job_inspection_report',
    'job_swms',
    'job_work_order',
  ],
  'checklists': [
    '*',
  ],
  'sites': [
    'site_asset_summary',
    'annual_condition_report',
    'summary_report'
  ],
  'quotes': [
    'quotation',
  ],
  'customer_invoices': [
    'customer_invoice_report'
  ],
  'supplier_invoices': [
    'supplier_invoice_report'
  ],
  'purchase_orders': [
    'purchase_order_report'
  ],
  'assets': [
    'asset_report'
  ]
};

/// contains the list of documents that is included in the html template
export const HTML_TEMPLATE_DOCUMENT_DEPENDENCIES = {
  'job_report': {
    'checklists': ['*'],
  },
};

export const FORCE_ENABLED_HTML_DOCUMENT_TYPES = {
  'jobs': [
    'job_inspection_report',
  ],
};

export const HTML_DOCUMENT_TYPE_OPTIONS: LooseObject = {
  jobs: [
    { id: 'job_report', text: 'job_report' },
    { id: 'job_swms', text: 'job_swms' },
    { id: 'job_work_order', text: 'job_work_order' },
    { id: 'job_inspection_report', text: 'job_inspection_report' },
  ],
  sites: [
    { id: 'site_asset_summary', text: 'site_asset_summary' },
    { id: 'site_annual_condition', text: 'site_annual_condition' },
    { id: 'summary_report', text: 'summary_report' },
  ],
  assets: [
    { id: 'asset_report', text: 'asset_report' },
  ],
  quotes: [
    { id: 'quotation', text: 'quotation' },
  ],
  supplier_invoices: [
    { id: 'supplier_invoice_report', text: 'supplier_invoice_report' },
  ],
  customer_invoices: [
    { id: 'customer_invoice_report', text: 'customer_invoice_report' },
  ],
  purchase_orders: [
    { id: 'purchase_order_report', text: 'purchase_order_report' },
  ],
  checklists: [
    { id: 'job_checklist', text: 'job_checklist' },
    { id: 'asset_checklist', text: 'asset_checklist' },
    { id: 'quote_checklist', text: 'quote_checklist' },
  ]
}

const modules: string[] = [
  'leads',
  'customers',
  'sites',
  'quotes',
  'jobs',
  'assets',
  'opportunities',
  'customer_invoices',
  'purchase_orders',
  'supplier_invoices',
  'checklists',
];

export const MODULE_OPTIONS: Select[] = modules.map((moduleName: string) => (new Select(moduleName, moduleName)));

export type HTML_TEMPLATE_TYPE = 'html_template';

export const HTML_TEMPLATE: HTML_TEMPLATE_TYPE = 'html_template';

export type ODT_TEMPLATE_TYPE = 'odt_template';

export const ODT_TEMPLATE: ODT_TEMPLATE_TYPE = 'odt_template';

/// This is due to the fact that the FormTemplate PHP model does not inherit from FieldmagicModel.
export const FORM_TEMPLATE_CONFIG = {
  used_fields: {
    template_type: {
      type: "dropdown",
      label: "template_type",
      required: true,
      default_value: HTML_TEMPLATE,
      in_sms_template: true,
      is_importable: true,
      import_header: "Template Type",
      allowed_bulk: true,
      deletable: false,
      readonly: false,
      options: [
          {
              id: ODT_TEMPLATE,
              text: ODT_TEMPLATE,
          },
          {
              id: HTML_TEMPLATE,
              text: HTML_TEMPLATE,
          },
      ]
    },
    is_active: {
      type: "checkbox",
      label: "is_active",
      required: false,
      default_value: true,
      is_importable: false,
      allowed_bulk: true,
      deletable: false,
      readonly: false
    },
    display_orientation: {
      type: "dropdown",
      label: "display_orientation",
      required: true,
      default_value: 'landscape',
      in_sms_template: true,
      is_importable: true,
      import_header: "Display Orientation",
      allowed_bulk: true,
      deletable: false,
      readonly: false,
      options: FORM_TEMPLATE_DISPLAY_ORIENTATION_LIST.map(strOrientation => ({
        id: strOrientation,
        text: strOrientation,
      })),
    },
    page_format: {
      type: "dropdown",
      label: "page_format",
      required: true,
      default_value: 'a4',
      in_sms_template: true,
      is_importable: true,
      import_header: "Page Format",
      allowed_bulk: true,
      deletable: false,
      readonly: false,
      options: PAGE_FORMATS_LIST.map(strOrientation => ({
        id: strOrientation,
        text: strOrientation,
      })),
    },
  }
};

export const sanitizeHtmlBeforeSaving = (strRawBody: string): string => {
  return strRawBody
    .replace(/<body[^>]*>([\s\S]*?)<\/body>/i, '$1')
    .replace(/<span class="remove-on-processing">.*?<\/span>/g, '')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    /// If there is custom logo, replace it with the liquid tag
    .replace(/<\s*img\b[^>]*\bclass\s*=\s*["'][^"']*?\bclient-logo-custom\b[^"']*?["'][^>]*>/g, clientLogoCustom);
}

export const sanitizeHtmlBeforeSendingToDocGenerator = (strRawBody: string): string => {
  return strRawBody
    /// Replace {[% operator %]} syntax to liquid parseable {% %}
    .replace(/\{\[\%\s*(.*?)\s*\%\]\}/g, '{%$1%}')
    /// Replace {[{ var }]} syntax to liquid parseable {{ }}.
    .replace(/\{\[\{\s*(.*?)\s*\}\]\}/g, '{{$1}}')
    /// remove comments for liquid js {% %} tags so that docgenerator can handle them
    .replace(/<!--\s*({%.*?%})\s*-->/g, '$1')
};

export const sanitizeCssBeforeSendingToDocGenerator = (strRawCss: string): string => {
  const strShorthandCss = compressCssShorthands(strRawCss);

  /// 99% width is to fix issue where the right side of the report has no border
  return strShorthandCss;
};

export const DEFAULT_MODULE_PLACEHOLDER_BLOCKS = {
  jobs: {
    label: 'job_report',
    block: 'job-report-block'
  },
  sites: {
    label: 'annual_condition_report',
    block: 'site-annual-condition-block'
  },
  checklists: {
    label: 'checklists',
    block: 'checklists-report-block'
  },
  assets: {
    label: 'asset_report',
    block: 'asset-report-block'
  },
  customer_invoices: {
    label: 'customer_invoice_report',
    block: 'customer-invoice-block'
  },
  supplier_invoices: {
    label: 'supplier_invoice_report',
    block: 'supplier-invoice-block'
  },
  purchase_orders: {
    label: 'purchase_order_report',
    block: 'purchase-order-block'
  },
  quotes: {
    label: 'quotes_',
    block: 'quote-version-report-block',
  },
};

export const DOCUMENT_TYPE_PLACEHOLDER_BLOCKS = {
  jobs: {
    job_report: {
      label: 'job_report',
      block: 'job-report-block',
    },
    job_swms: {
      label: 'swms_pdf',
      block: 'job-swms-block',
    },
    job_inspection_report: {
      label: 'job_inspection_report',
      block: 'job-inspection-summary-report-block'
    },
    job_work_order: {
      label: 'work_order',
      block: 'job-work-order-block',
    },
  },
  sites: {
    site_annual_condition: {
      label: 'annual_condition_report',
      block: 'site-annual-condition-block'
    },
    summary_report: {
      label: 'site_service_summary_report',
      block: 'site-service-summary-block'
    },
    site_asset_summary: {
      label: 'asset_summary_report',
      block: 'site-asset-summary-block',
    }
  },
  checklists: {
    checklists: {
      label: 'checklists',
      block: 'checklists-report-block',
    }
  },
  assets: {
    asset_report: {
      label: 'asset_report',
      block: 'asset-report-block',
    }
  },
  customer_invoices: {
    customer_invoice_report: {
      label: 'customer_invoice_report',
      block: 'customer-invoice-block',
    }
  },
  supplier_invoices: {
    supplier_invoice_report: {
      label: 'supplier_invoice_report',
      block: 'supplier-invoice-block'
    }
  },
  purchase_orders: {
    purchase_order_report: {
      label: 'purchase_order_report',
      block: 'purchase-order-block'
    }
  },
  opportunities: {
    quotation: {
      label: 'quotes_',
      block: 'quote-version-report-block',
    }
  },
  quotes: {
    quotation: {
      label: 'quotes_',
      block: 'quote-version-report-block',
    }
  },
};

export const generateBlockColumn = (numColumn: number, options: {
  is_default_container?: boolean,
  default_content?: string,
} = {}): string => {
  const bIsDefaultContainer = data_is_true(data_get(options, 'is_default_container'));

  const defaultClass = when(bIsDefaultContainer, {
    then: () => 'default-body-container',
    else: () => '',
  });

  const defaultContent = when(bIsDefaultContainer, {
    then: () => data_get(options, 'default_content', { default_value: '' }),
    else: () =>  '',
  });

  let strColumns = '';

  for (let i=0; i<numColumn; i++) {
    strColumns += `<div class="gjs-cell" data-gjs-draggable=".gjs-row" data-gjs-resizable='{"tl":0,"tc":0,"tr":0,"cl":1,"cr":1,"bl":0,"br":0,"minDim":1,"bc":0,"currentUnit":1,"step":0.2}' data-gjs-name="Cell" data-gjs-unstylable='["width"]'>${defaultContent}</div>`;
  }

  return `
    <div class="gjs-row ${defaultClass}" data-gjs-droppable=".gjs-cell" data-gjs-resizable='{"tl":0,"tc":0,"tr":0,"cl":0,"cr":0,"bl":0,"br":0,"minDim":1}' data-gjs-name="Row">
      ${strColumns}
    </div>
    <style>
      .gjs-row {
        padding-top:10px;
        padding-right:10px;
        padding-bottom:10px;
        padding-left:10px;
        display:table;
        width: 100%;
      }

      .gjs-cell {
        width: 8%;
        display: table-cell;
        height:  75px;
      }

      @media (max-width: 576px){
        .gjs-cell{
          width: 100%;
          display: block;
        }
      }
    </style>
  `;
}

export const generatePlaceholderBlocks = (strClassName: string, strLabel: string): string => {
  return `
    <div class="${strClassName} custom-placeholder-block"
      data-gjs-name="Div">
      ${strLabel}
    </div>
  `;
}

export class ParseableData {
  client: ParseableDataClient;

  constructor (data: LooseObject) {
    this.client = {
      specialisation: data_get(data, 'config.pdf.company_specialisation'),
      enabled_page_numbers_raw: data_get(data, 'config.pdf.enable_page_numbers', { default_value: true }),
      footer_text:  data_get(data, 'config.pdf.company_footer_text'),
      footer_website:  data_get(data, 'config.pdf.company_footer_website'),
      phone: data_get(data, 'config.pdf.company_phone'),
      email: data_get(data, 'config.pdf.company_email'),
      logo: data_get(data, 'config.pdf.company_logo.upload_name'),
      address: data_get(data, 'address_text'),
    };
  }

  setLogo(strLink: string): void {
    this.client.logo = strLink;
  }
}

export interface ParseableDataClient {
  specialisation: string|null,
  enabled_page_numbers_raw: boolean,
  footer_text: string|null,
  footer_website: string|null,
  phone: string|null,
  email: string|null,
  logo: string|null,
  address: string|null,
}

export const USE_EXISTING_ODT_TEMPLATE = 'use_existing_odt_template';

export const UPLOAD_NEW_ODT_TEMPLATE = 'upload_new_odt_template';

export const HTML_BUILDER_BLOCK_PLACEHOLDER_IDS = [
  "job-report-block-container",
  "site-asset-summary-block-container",
  "job-inspection-summary-report-block-container",
  "quote-version-report-block-container",
  "checklists-report-block-container",
  "job-swms-block-container",
  "site-service-summary-block-container",
  "site-annual-condition-block-container",
  "customer-invoice-block-container",
  "supplier-invoice-block-container",
  "purchase-order-block-container",
  "asset-report-block-container",
  "job-work-order-block-container",
];

/**
 * Compresses duplicate css like
 *
 * padding-top:12px;
 * padding-right:12px;
 * padding-bottom:12px;
 * padding-left:12px;
 *
 * into just:
 *
 * padding: 12px;
 *
 * @param {string} cssText
 *
 * @returns {string}
 */
export const compressCssShorthands = (cssText: string): string => {
  // Helper: check if all sides equal
  const allEqual = (item) => item[0] === item[1] && item[1] === item[2] && item[2] === item[3];

  return cssText.replace(/(#[\w-]+)\s*\{([^}]*)\}/g, (match, selector, body) => {
    const rules = {};
    body.split(';').forEach(decl => {
      const [prop, val] = decl.split(':').map(s => s.trim());
      if (prop && val) rules[prop] = val;
    });

    // ---- Border shorthand ----
    if (
      rules['border-top-width'] &&
      rules['border-right-width'] &&
      rules['border-bottom-width'] &&
      rules['border-left-width'] &&
      rules['border-top-style'] &&
      rules['border-right-style'] &&
      rules['border-bottom-style'] &&
      rules['border-left-style'] &&
      rules['border-top-color'] &&
      rules['border-right-color'] &&
      rules['border-bottom-color'] &&
      rules['border-left-color']
    ) {
      const widths = [
        rules['border-top-width'],
        rules['border-right-width'],
        rules['border-bottom-width'],
        rules['border-left-width']
      ];
      const styles = [
        rules['border-top-style'],
        rules['border-right-style'],
        rules['border-bottom-style'],
        rules['border-left-style']
      ];
      const colors = [
        rules['border-top-color'],
        rules['border-right-color'],
        rules['border-bottom-color'],
        rules['border-left-color']
      ];

      if (allEqual(widths) && allEqual(styles) && allEqual(colors)) {
        let color = colors[0];

        const border = `${widths[0]} ${styles[0]} ${color}`;
        // Remove longhand border props
        ['border-top-width', 'border-top-style', 'border-top-color',
         'border-right-width', 'border-right-style', 'border-right-color',
         'border-bottom-width', 'border-bottom-style', 'border-bottom-color',
         'border-left-width', 'border-left-style', 'border-left-color',
         'border-image-source', 'border-image-slice', 'border-image-width',
         'border-image-outset', 'border-image-repeat'
        ].forEach(p => delete rules[p]);
        rules['border'] = border;
      }
    }

    // ---- Padding shorthand ----
    if (
      rules['padding-top'] &&
      rules['padding-right'] &&
      rules['padding-bottom'] &&
      rules['padding-left']
    ) {
      const paddings = [
        rules['padding-top'],
        rules['padding-right'],
        rules['padding-bottom'],
        rules['padding-left']
      ];
      if (allEqual(paddings)) {
        rules['padding'] = paddings[0];
        ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'].forEach(p => delete rules[p]);
      }
    }

    // ---- Border-radius shorthand ----
    if (
      rules['border-top-left-radius'] &&
      rules['border-top-right-radius'] &&
      rules['border-bottom-right-radius'] &&
      rules['border-bottom-left-radius']
    ) {
      const radii = [
        rules['border-top-left-radius'],
        rules['border-top-right-radius'],
        rules['border-bottom-right-radius'],
        rules['border-bottom-left-radius']
      ];
      if (allEqual(radii)) {
        rules['border-radius'] = radii[0];
        ['border-top-left-radius', 'border-top-right-radius',
         'border-bottom-right-radius', 'border-bottom-left-radius'
        ].forEach(p => delete rules[p]);
      }
    }

    // ---- Background shorthand (basic) ----
    if (rules['background-color']) {
      // Remove redundant background-*: initial props if background-color exists
      const bgProps = [
        'background-image', 'background-position-x', 'background-position-y',
        'background-size', 'background-repeat', 'background-attachment',
        'background-origin', 'background-clip'
      ];
      let hasNonInitial = false;
      for (const p of bgProps) {
        if (rules[p] && rules[p] !== 'initial') {
          hasNonInitial = true;
          break;
        }
      }
      if (!hasNonInitial) {
        bgProps.forEach(p => delete rules[p]);
        // Keep only background-color, optionally rename to 'background'
        rules['background'] = rules['background-color'];
        delete rules['background-color'];
      }
    }

    // Rebuild body
    const newBody = Object.entries(rules)
      .map(([prop, val]) => `${prop}: ${val};`)
      .join(' ');

    return `${selector} { ${newBody} }`;
  });
}

export const DEFAULT_HTML_GRAPES_CSS = '* { box-sizing: border-box; } body {margin: 0;}.custom-placeholder-block { height: 50px; width: 100%; text-align: center; background-color: #0e76bc; color: white; line-height: 50px; border: 1px solid #efefef; }.gjs-row{padding-top:10px;padding-right:10px;padding-bottom:10px;padding-left:10px;display:table;width:100%;}.gjs-cell{width:8%;display:table-cell;height:75px;}#ix3ib{padding-bottom:0.50rem;padding-top:0.50rem;}#i3br9{vertical-align:middle;}#i13lm{height:50px;width:100%;text-align:center;background-color:#0e76bc;color:white;line-height:50px;border:1px solid #efefef;}#footer{width:100%;font-size:12px;display:table;margin-top:20px;}#footer_left{display:table-cell;padding-left:20px;}#footer_right{display:table-cell;text-align:right;padding-right:20px;}@media (max-width: 576px){.gjs-cell{width:100%;display:block;}}';